/**
 * AuthGuard - The Intelligent Standard
 *
 * Route protection with branded loading state and optional modal authentication.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from './LoginModal';
import { Logo } from './Logo';

interface AuthGuardProps {
  children: ReactNode;
  redirectTo?: string;
  showModal?: boolean; // If true, show login modal instead of redirecting
}

export function AuthGuard({
  children,
  redirectTo = '/login',
  showModal = false,
}: AuthGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: '#F5F0EB' }}
      >
        <div className="mb-8">
          <Logo size={48} variant="default" />
        </div>
        <div className="flex items-center gap-3" style={{ color: '#6B6B6B' }}>
          <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="60"
              strokeDashoffset="20"
              opacity="0.3"
            />
          </svg>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    // Show modal login for user-facing routes
    if (showModal) {
      return (
        <>
          {children}
          <LoginModal onClose={() => window.history.back()} />
        </>
      );
    }

    // Redirect to login page for admin/protected routes
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
